import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { fiSetBrandingColor, fiSetContrastColor, isDefined } from '@fi-shared/utils';
import { FiImageSize } from '@fi-shared/types';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

export interface IBrandPreviewData {
    primaryColor: string;
    contrastColor: string;
    logoRef: IMediaBankFileReference;
    landscapeRef: IMediaBankFileReference;
}

@Component({
    selector: 'fi-site-design-brand-preview-dialog',
    templateUrl: './fi-site-design-brand-preview-dialog.component.html',
    styleUrls: ['./fi-site-design-brand-preview-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-site-design-brand-preview-dialog' },
    standalone: false,
})
export class FiSiteDesignBrandPreviewDialogComponent
    extends FiDialogsContentBase<IBrandPreviewData, boolean>
    implements OnInit
{
    @ViewChild('desktopBg', { static: true }) desktopBg: ElementRef;

    private elementRef: ElementRef;
    private mediaStorageService: FiMediaStorageService;
    private sanitizer: DomSanitizer;

    ngOnInit(): void {
        this.setServices();
        this.setPreviewImagesAndColor();
    }

    getDesktopLoginStyleTransform(): SafeStyle {
        const scale: number = this.getDesktopLoginWidthK();
        return this.sanitizer.bypassSecurityTrustStyle(`scale(${scale}) translateX(-50%)`);
    }

    private setPreviewImagesAndColor() {
        const brandingStyle: HTMLStyleElement = fiSetBrandingColor(
            this.data.primaryColor,
            'fi-site-design-brand-preview-dialog',
            this.elementRef.nativeElement,
        );

        if (isDefined(this.data.landscapeRef)) {
            // note: didn't change AttachmentPreview to Small, because of file is absent
            const landscapeBackground: string = this.getMediaFileUrl(this.data.landscapeRef, FiImageSize.Background);
            brandingStyle.appendChild(
                document.createTextNode(`:root{ 
                --fi-branded-image-landscape: ${landscapeBackground};
            }`),
            );
        } else {
            brandingStyle.appendChild(
                document.createTextNode(`:root{ 
                --fi-branded-image-landscape: none;
            }`),
            );
        }

        if (isDefined(this.data.logoRef)) {
            const logoBackground: string = this.getMediaFileUrl(this.data.logoRef, FiImageSize.Logo);
            brandingStyle.appendChild(
                document.createTextNode(`:root{ 
                --fi-branded-image-logo: ${logoBackground};
            }`),
            );
        } else {
            brandingStyle.appendChild(
                document.createTextNode(`:root{ 
                --fi-branded-image-logo: none;
            }`),
            );
        }

        fiSetContrastColor(
            this.data.contrastColor,
            'fi-site-design-brand-preview-dialog',
            this.elementRef.nativeElement,
        );
    }

    private getMediaFileUrl(mediaBankFileReference: IMediaBankFileReference, imageSize: FiImageSize) {
        return `url("${this.mediaStorageService.getMediaFileUrl(mediaBankFileReference, { size: imageSize })}")`;
    }

    private getDesktopLoginWidthK(): number {
        const scale: number = this.desktopBg.nativeElement.offsetWidth / 1800;
        return scale < 1 ? scale : 1;
    }

    private setServices() {
        this.elementRef = this.injector.get(ElementRef);
        this.mediaStorageService = this.injector.get(FiMediaStorageService);
        this.sanitizer = this.injector.get(DomSanitizer);
    }
}
